import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import Card from "../../components/UI/Card";
import Button from "../../components/UI/Button";
import Alert from "../../components/UI/Alert";
import globalStyle from "../../styles/globalStyle";
import authService from "../../services/authService";

import logo from "../../assets/logo.png";

interface ResetPasswordForm {
  otp: string;
  password: string;
  password_confirmation: string;
}

const ResetPassword: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  // eslint-disable-next-line
  const [email, setEmail] = useState("");
  const [resetData, setResetData] = useState<{
    user_id: number;
    reset_token: string;
  } | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ResetPasswordForm>();
  const password = watch("password");
  const siteName = process.env.REACT_APP_SITE_NAME || "XRP Trading Platform";

  useEffect(() => {
    const state = location.state;
    if (state?.email && state?.reset_token && state?.user_id) {
      setEmail(state.email);
      setResetData({
        user_id: state.user_id,
        reset_token: state.reset_token,
      });
    } else {
      navigate("/forgot-password");
    }
  }, [location, navigate]);

  const onSubmit = async (data: ResetPasswordForm) => {
    if (!resetData) {
      setError("Invalid reset session");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await authService.resetPassword({
        ...resetData,
        password: data.password,
        password_confirmation: data.password_confirmation,
      });
      setSuccess(true);
      setTimeout(() => navigate("/login"), 3000);
    } catch (err: any) {
      setError(err.message || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={globalStyle.container}>
      {/* <Header /> */}

      <main className="page-content2">
        <div className="page-content2-bg"></div>
        <div className="page-content2-wrapper">
          <div className="page-content2-content">
            <div className="max-w-md mx-auto py-8 md:py-12">
              <Link to="/" className="text-center mb-8">
                <img
                  src={logo}
                  alt={siteName}
                  className="w-16 h-16 mx-auto mb-4"
                />
                <h2 className={globalStyle.h3}>Reset Password</h2>
                <p className={globalStyle.body2 + " text-gray-400"}>
                  Create a new password for your account
                </p>
              </Link>

              <Card padding="lg" className="mt-6">
                {error && (
                  <Alert
                    type="error"
                    message={error}
                    onClose={() => setError(null)}
                    className="mb-6"
                  />
                )}

                {success ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 rounded-full bg-green-900 flex items-center justify-center mx-auto mb-4">
                      <svg
                        className="w-8 h-8 text-green-300"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                    <h3 className={globalStyle.h5}>Password Reset!</h3>
                    <p className={`${globalStyle.body2} mt-2 text-gray-400`}>
                      Your password has been successfully updated.
                    </p>
                    <p className={`${globalStyle.caption} mt-4 text-gray-500`}>
                      Redirecting to login...
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                    <div className={globalStyle.formGroup}>
                      <label className={globalStyle.label}>New Password</label>
                      <div className="relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          className={globalStyle.input}
                          placeholder="Create a strong password"
                          {...register("password", {
                            required: "Password is required",
                            minLength: {
                              value: 8,
                              message: "Password must be at least 8 characters",
                            },
                          })}
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-300"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? "Hide" : "Show"}
                        </button>
                      </div>
                      {errors.password && (
                        <p className={globalStyle.formError}>
                          {errors.password.message}
                        </p>
                      )}
                      <p
                        className={`${globalStyle.caption} mt-2 text-gray-400`}
                      >
                        Must be at least 8 characters
                      </p>
                    </div>

                    <div className={globalStyle.formGroup}>
                      <label className={globalStyle.label}>
                        Confirm New Password
                      </label>
                      <div className="relative">
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          className={globalStyle.input}
                          placeholder="Confirm your password"
                          {...register("password_confirmation", {
                            required: "Please confirm your password",
                            validate: (value) =>
                              value === password || "Passwords do not match",
                          })}
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-300"
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                        >
                          {showConfirmPassword ? "Hide" : "Show"}
                        </button>
                      </div>
                      {errors.password_confirmation && (
                        <p className={globalStyle.formError}>
                          {errors.password_confirmation.message}
                        </p>
                      )}
                    </div>

                    <div className="bg-blue-900 border border-blue-800 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-blue-300 mb-2">
                        Password Requirements
                      </h4>
                      <ul className="text-sm text-blue-200 space-y-1">
                        <li>• At least 8 characters long</li>
                        <li>• Include uppercase and lowercase letters</li>
                        <li>
                          • Include numbers and symbols for better security
                        </li>
                        <li>• Avoid using personal information</li>
                      </ul>
                    </div>

                    <Button type="submit" fullWidth loading={loading} size="lg">
                      Reset Password
                    </Button>
                  </form>
                )}

                <div className="mt-8 pt-6 border-t border-gray-800">
                  <p className="text-center text-sm text-gray-400">
                    Remember your password?{" "}
                    <Link
                      to="/login"
                      className="text-primary font-medium hover:text-primary-300"
                    >
                      Sign in
                    </Link>
                  </p>
                </div>
              </Card>

              <div className="mt-8 text-center">
                <p className={globalStyle.caption + " text-gray-500"}>
                  For your security, please create a strong and unique password
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ResetPassword;
